import React, { useState, useEffect } from "react";
import { cn } from "~/lib/utils";

// // // //

type Duration = 75 | 100 | 150 | 200 | 300 | 500 | 700 | 1000;

interface Props {
    show?: boolean;
    duration?: Duration;
    children: React.ReactNode;
    className?: string;
}

/**
 * A map of durations to Tailwind class names.
 * This ensures Tailwind's JIT engine detects and includes these classes.
 */
const durationClasses = {
    75: "duration-75",
    100: "duration-100",
    150: "duration-150",
    200: "duration-200",
    300: "duration-300",
    500: "duration-500",
    700: "duration-700",
    1000: "duration-1000",
};

/**
 * FadeIn
 * Implements a pure React & Tailwind CSS transition to fade-in children elements.
 * Replaces @headlessui/react Transition while maintaining a similar API.
 */
export function FadeIn(props: Props) {
    const { show = true, duration = 500, children, className } = props;

    // tracks if the component has completed its first mount "appear" trigger
    const [isRendered, setIsRendered] = useState(false);

    useEffect(() => {
        // We use a small timeout/RAF to ensure the browser has painted the initial 0 opacity state
        // before we switch to 100, triggering the CSS transition.
        const timeout = setTimeout(() => {
            setIsRendered(true);
        }, 10);
        return () => clearTimeout(timeout);
    }, []);

    // Determine the effective visibility
    const isVisible = show && isRendered;

    return (
        <div
            className={cn(
                "transition-opacity ease-in-out",
                durationClasses[duration],
                {
                    "opacity-100 pointer-events-auto": isVisible,
                    "opacity-0 pointer-events-none": !isVisible,
                },
                className,
            )}
            aria-hidden={!isVisible}
        >
            {children}
        </div>
    );
}
